import {
  CopyIcon,
  DownloadIcon,
  Pencil2Icon,
  TrashIcon,
} from '@radix-ui/react-icons';
import { Badge, Flex } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { SuperAdminIcon } from 'components/common/custom-icon/shorthands';
import { AssignPitcherDialog } from 'components/common/dialogs/assign-pitcher';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonTableHoC } from 'components/common/table';
import { VideoErrorsIcon } from 'components/common/video/error-icon';
import { VideoFrameRateBadge } from 'components/common/video/frame-rate-badge';
import { VideoResolutionBadge } from 'components/common/video/resolution-badge';
import { VideoShareIcon } from 'components/common/video/share-icon';
import { VideoStatsApiIcon } from 'components/common/video/stats-api-icon';
import { PAGE_SIZES } from 'components/sections/admin-portal/machines/dialogs/settings/machine-session-history/common';
import { VideoLibraryTableToolbar } from 'components/sections/video-library/table/toolbar';
import { MachineContext } from 'contexts/machine.context';
import { lightFormat } from 'date-fns';
import { ACTIONS_KEY, CrudAction } from 'enums/tables';
import { t } from 'i18next';
import { PaginationID } from 'interfaces/cookies/i-app.cookie';
import { IAssignPitcher } from 'interfaces/i-assign-pitcher';
import { IMenuAction } from 'interfaces/i-menus';
import { ITableCheckable } from 'interfaces/tables/checking';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { ITablePageable } from 'interfaces/tables/pagination';
import { ITableSelectable } from 'interfaces/tables/selection';
import { ITableSortable } from 'interfaces/tables/sorting';
import { MiscHelper } from 'lib_ts/classes/misc.helper';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { VideoHelper } from 'lib_ts/classes/video.helper';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IMachine } from 'lib_ts/interfaces/i-machine';
import { IVideo } from 'lib_ts/interfaces/i-video';
import { first } from 'lodash';
import { useCallback, useContext, useMemo, useState } from 'react';
import { VideosService } from 'services/videos.service';
import { useAuthStore } from 'stores/use-auth-store';
import { STATIC_PREFIX, useVideosStore } from 'stores/use-videos-store';

const COMPONENT_NAME = 'VideoLibraryTable';

export const VideoLibraryTable = (props: {
  videos: IVideo[];
  // toggles on static type column
  static: boolean;
  afterChangeSelected: (video: IVideo | undefined) => void;
}) => {
  const userRoleFeatureFlags = useAuthStore((s) => s.userRoleFeatureFlags);

  const { machine, update } = useContext(MachineContext);

  const copyVideos = useCallback(async (ids: string[]) => {
    try {
      useVideosStore.setState({ loading: true });

      const result = await VideosService.getInstance().copyMany(ids);

      if (!result.success) {
        throw new Error(result.error);
      }

      /** append to end */
      const newVideos = result.data as IVideo[];

      useVideosStore.setState((s) => ({
        videos: [...s.videos, ...newVideos],
      }));

      NotifyHelper.success({
        message_md: t('common.x-copied-successfully', {
          x: t(ids.length === 1 ? 'videos.video' : 'videos.videos'),
        }),
      });

      return true;
    } catch (e) {
      console.error(e);

      NotifyHelper.error({
        message_md: t('common.request-failed-msg'),
      });

      return false;
    } finally {
      useVideosStore.setState({ loading: false });
    }
  }, []);

  const [dialogAssignPitcher, setDialogAssignPitcher] = useState<{
    key: number;
    value: IAssignPitcher;
    checkedVideoIDs: string[];
  }>();

  const columns = useMemo<ITypedTableColumn<IVideo>[]>(
    () => [
      {
        label: 'common.created',
        key: '_created',
        dataType: 'datetime',
        invisible: true,
      },
      {
        label: 'videos.video-title',
        key: 'VideoTitle',
        sortRowsFn: (a, b, dir) =>
          -StringHelper.safeSort(a.VideoTitle, b.VideoTitle) * dir,
      },
      {
        label: 'common.pitcher',
        key: 'PitcherFullName',
        sortRowsFn: (a, b, dir) =>
          -StringHelper.safeSort(a.PitcherFullName, b.PitcherFullName) * dir,
      },
      {
        label: 'common.pitch-type',
        key: 'PitchType',
        sortRowsFn: (a, b, dir) =>
          -StringHelper.safeSort(a.PitchType, b.PitchType) * dir,
      },
      {
        label: 'videos.delivery',
        key: 'DeliveryType',
        sortRowsFn: (a, b, dir) =>
          -StringHelper.safeSort(a.DeliveryType, b.DeliveryType) * dir,
      },
      {
        label: 'common.last-updated',
        key: '_changed',
        dataType: 'datetime',
      },
      {
        label: 'common.updated-by',
        key: 'changer',
        sortRowsFn: (a, b, dir) =>
          -StringHelper.safeSort(a.changer, b.changer) * dir,
      },
      {
        label: 'videos.resolution-and-rate',
        key: 'resolution',
        formatFn: (v) => (
          <Flex gap="1">
            <VideoResolutionBadge value={v} />
            <VideoFrameRateBadge value={v.fps} />
          </Flex>
        ),
      },
      {
        invisible: !props.static,
        label: 'Type',
        key: 'static_type',
        formatFn: (m) =>
          m.static_type ? <Badge>{m.static_type}</Badge> : '--',
      },
      {
        label: '',
        key: '_errors_and_shared',
        formatFn: (v) => (
          <Flex gap="2">
            <VideoStatsApiIcon video={v} />
            <VideoErrorsIcon video={v} />
            <VideoShareIcon video={v} />
          </Flex>
        ),
        sortRowsFn: (a, b, dir: number) => {
          const [aErrors, bErrors] = [a, b].map(
            (m) => VideoHelper.getErrors(m).length
          );

          // sort by diff errors first
          if (aErrors !== bErrors) {
            return (aErrors > bErrors ? -1 : 1) * dir;
          }

          // sort by shared or not second
          return (
            ((a.original_id ? 1 : 0) > (b.original_id ? 1 : 0) ? -1 : 1) * dir
          );
        },
      },
      {
        label: '',
        key: ACTIONS_KEY,
        actions: [
          {
            label: 'common.set-as-default',
            color: RADIX.COLOR.WARNING,
            // hide option when already the default
            invisibleFn: (video) => {
              const isLHP = video.ReleaseSide > 0;

              if (isLHP) {
                return video._id === machine.default_video_lhp;
              }

              return video._id === machine.default_video_rhp;
            },
            onClick: async (video) => {
              const errors = VideoHelper.getErrors(video);
              if (errors.length > 0) {
                NotifyHelper.warning({
                  message_md: t('videos.default-video-issues-msg'),
                });
                return;
              }

              const payload: Partial<IMachine> = {
                _id: machine._id,
              };

              const isLHP = video.ReleaseSide > 0;
              if (isLHP) {
                payload.default_video_lhp = video._id;
              } else {
                payload.default_video_rhp = video._id;
              }

              const success = await update(payload);
              if (!success) {
                return;
              }

              NotifyHelper.info({
                message_md: t('videos.updated-default-video-for-x', {
                  x: machine.machineID,
                }),
              });

              // force default star to move
              // todo: do we need this anymore
              // this.props.tableCx.updateTableKey();
            },
          },
          {
            label: 'common.duplicate',
            onClick: (video) => copyVideos([video._id]),
          },
          {
            label: 'common.download',
            suffixIcon: <SuperAdminIcon />,
            onClick: async (video) => {
              NotifyHelper.info({
                message_md: 'Preparing your download, please wait...',
              });

              const playback = await useVideosStore
                .getState()
                .lazyLoadPlayback(video._id);

              if (playback?.video.url) {
                window.open(playback.video.url);
              }
            },
            disableFn: (video) => !video.video_path,
            invisibleFn: () => !userRoleFeatureFlags.controls.downloadVideo,
          },
          {
            label: 'common.delete',
            color: RADIX.COLOR.DANGER,
            disableFn: (video) => video.video_path.startsWith(STATIC_PREFIX),
            onClick: (video) =>
              useVideosStore.setState({
                dialogCRUD: {
                  key: Date.now(),
                  action: CrudAction.Delete,
                  models: [video],
                },
              }),
          },
        ],
      },
    ],
    [
      props.static,
      userRoleFeatureFlags,
      machine._id,
      machine.machineID,
      machine.default_video_lhp,
      machine.default_video_rhp,
    ]
  );

  const pagination: ITablePageable = {
    paginationID: PaginationID.VideoList,
    total: props.videos.length,
    enablePagination: true,
    pageSizes: PAGE_SIZES,
  };

  const sort: ITableSortable = {
    defaultSort: { key: '_created', dir: 1 },
    enableSort: true,
  };

  const select: ITableSelectable = {
    enableSelect: true,
    afterChangeSelected: props.afterChangeSelected,
  };

  const checkable: ITableCheckable = {
    checkboxColumnIndex: 0,
    checkedActions: (checked: IVideo[]): IMenuAction[] => {
      const removable = checked.filter(
        (v) => !v.video_path.startsWith(STATIC_PREFIX)
      );

      const output: IMenuAction[] = [
        {
          prefixIcon: <Pencil2Icon />,
          label: 'common.assign-pitcher',
          invisible: checked.length === 0,
          onClick: () => {
            const firstChecked = first(checked);

            setDialogAssignPitcher({
              key: Date.now(),
              value: {
                mlb: firstChecked?.mlb_pitcher ?? null,
                name: firstChecked?.PitcherFullName ?? null,
                team: firstChecked?.TeamName ?? null,
                teamPk: null, // not relevant for videos
              },
              checkedVideoIDs: checked.map((v) => v._id),
            });
          },
        },
        {
          prefixIcon: <DownloadIcon />,
          label: 'common.export-selected',
          invisible: checked.length === 0,
          onClick: () => {
            const ids = checked.map((v) => v._id);

            VideosService.getInstance()
              .exportCsv(ids)
              .then((csvString) => {
                const blob = new Blob([csvString], { type: 'text/csv' });
                MiscHelper.saveAs(
                  blob,
                  `video-library_checked_${lightFormat(
                    new Date(),
                    'yyyy-MM-dd'
                  )}.csv`
                );
              });
          },
        },
        {
          prefixIcon: <CopyIcon />,
          label: 'common.duplicate-selected',
          color: RADIX.COLOR.WARNING,
          invisible: checked.length === 0,
          onClick: () => copyVideos(checked.map((v) => v._id)),
        },
        {
          prefixIcon: <TrashIcon />,
          label: 'common.delete-selected',
          color: RADIX.COLOR.DANGER,
          invisible: removable.length === 0,
          onClick: () =>
            useVideosStore.setState({
              dialogCRUD: {
                key: Date.now(),
                action: CrudAction.Delete,
                models: removable,
              },
            }),
        },
      ];

      return output;
    },
  };

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <CommonTableHoC
        dataTestID={COMPONENT_NAME}
        identifier={COMPONENT_NAME + 'Table'}
        data-testid={COMPONENT_NAME}
        rowTestLocatorFn={(v: IVideo) =>
          VideoHelper.getErrors(v).length === 0 ? 'no-issues' : 'has-issues'
        }
        displayColumns={columns}
        displayData={props.videos}
        toolbarContent={props.static ? undefined : <VideoLibraryTableToolbar />}
        // skipResetSelected // * so that updating a video via sidebar does not unselect the row
        {...pagination}
        {...checkable}
        {...select}
        {...sort}
        vFlex
      />

      {dialogAssignPitcher && (
        <AssignPitcherDialog
          key={dialogAssignPitcher.key}
          defaultValue={dialogAssignPitcher.value}
          onChange={(value) => {
            useVideosStore.getState().updateVideos(
              dialogAssignPitcher.checkedVideoIDs.map((id) => ({
                _id: id,
                mlb_pitcher: value.mlb,
                PitcherFullName: value.name,
                TeamName: value.team,
              }))
            );
          }}
          onClose={() => setDialogAssignPitcher(undefined)}
        />
      )}
    </ErrorBoundary>
  );
};
